'use client';

import React, { createContext, useContext, useState } from 'react';
import type { TTSVoice } from '@/shared/components/dialog/TtsDialog/TtsDialogContext';
import type { BrandTemplateBoundaryVideo } from '@/features/brand-template/model/brand-template';

interface BrandTemplateSettingsContextValue {
  // TTS Voice 설정
  ttsVoice: TTSVoice | null;
  setTtsVoice: (voice: TTSVoice | null) => void;

  // Script 설정 (intro/outro 텍스트)
  introText: string;
  outroText: string;
  setIntroText: (text: string) => void;
  setOutroText: (text: string) => void;

  // Boundary video 설정
  introVideo: BrandTemplateBoundaryVideo | null;
  outroVideo: BrandTemplateBoundaryVideo | null;
  setIntroVideo: (video: BrandTemplateBoundaryVideo | null) => void;
  setOutroVideo: (video: BrandTemplateBoundaryVideo | null) => void;

  // Video Aspect Ratio 설정
  videoAspectRatio: '16:9' | '9:16';
  setVideoAspectRatio: (ratio: '16:9' | '9:16') => void;
}

const BrandTemplateSettingsContext =
  createContext<BrandTemplateSettingsContextValue | null>(null);

export function BrandTemplateSettingsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ttsVoice, setTtsVoice] = useState<TTSVoice | null>(null);
  const [introText, setIntroText] = useState<string>('');
  const [outroText, setOutroText] = useState<string>('');
  const [introVideo, setIntroVideo] =
    useState<BrandTemplateBoundaryVideo | null>(null);
  const [outroVideo, setOutroVideo] =
    useState<BrandTemplateBoundaryVideo | null>(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState<'16:9' | '9:16'>(
    '16:9',
  );

  return (
    <BrandTemplateSettingsContext.Provider
      value={{
        ttsVoice,
        setTtsVoice,
        introText,
        outroText,
        setIntroText,
        setOutroText,
        introVideo,
        outroVideo,
        setIntroVideo,
        setOutroVideo,
        videoAspectRatio,
        setVideoAspectRatio,
      }}
    >
      {children}
    </BrandTemplateSettingsContext.Provider>
  );
}

export function useBrandTemplateSettings() {
  const context = useContext(BrandTemplateSettingsContext);
  if (!context) {
    throw new Error(
      'useBrandTemplateSettings must be used within BrandTemplateSettingsProvider',
    );
  }
  return context;
}
